import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowUturnLeftIcon, LockClosedIcon } from '@heroicons/react/20/solid';
import { Button } from '../../../components/UI';
import { RouteConstants } from '../../../route-constants';
import { useLogin } from '../../../services/login/login';
import { isMktError, MktError, MktOAuthRequestView, MktScope, useMarketing } from '../../../services/marketing/marketing';
import { PublicMessage, PublicShell } from '../public/components/PublicShell.component';
import { LAUNCH_LIMIT_ID, normaliseScopes, SCOPES } from '../settings/ai_keys';
import { LaunchWarning, ScopePicker } from '../settings/components/ScopePicker.component';
import { chipClass } from '../templates/utils/styles';

const EXPIRY_CHOICES = [30, 90, 365];
const DEFAULT_EXPIRY = 90;

// #region orderScopes
/** Read first and the rest in the usual order; anything Wallmates does not know goes last, by name. */
const orderScopes = (requested: string[]): string[] => {
    const known: string[] = SCOPES.map((s) => s.scope);
    const set = new Set(['read', ...requested]);
    return [...known.filter((s) => set.has(s)), ...requested.filter((s) => !known.includes(s))];
};

// #region ConsentForm
const ConsentForm: React.FunctionComponent<{ request: string; req: MktOAuthRequestView; email: string; onSwitchUser: () => void }> = ({ request, req, email, onSwitchUser }): React.ReactElement => {
    const [api] = useMarketing();
    const grantable = React.useMemo(() => ['read', ...req.grantable], [req.grantable]);
    const [scopes, setScopes] = React.useState<MktScope[]>(() => normaliseScopes(req.scopes.filter((s) => grantable.includes(s)) as MktScope[]));
    const [days, setDays] = React.useState(DEFAULT_EXPIRY);
    const [busy, setBusy] = React.useState<'allow' | 'cancel' | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    const finish = async (which: 'allow' | 'cancel'): Promise<void> => {
        setBusy(which);
        setError(null);
        const r = which === 'allow'
            ? await api.oauthApprove({ request, scopes: scopes.filter((s) => grantable.includes(s)), expires_in_days: days })
            : await api.oauthDeny(request);
        if (isMktError(r)) { setBusy(null); setError(r.message); return; }
        // Stays busy: the browser is on its way back to the app.
        window.location.assign(r.redirect);
    };

    return (
        <div className="flex flex-col gap-5 px-5 py-6 sm:px-6">
            <div className="text-center">
                <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
                    {req.client_name} wants to connect to {req.workspace}
                </h1>
                <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-400">Choose what it may do. You can disconnect it any time in Marketing settings, under AI connection.</p>
            </div>

            <fieldset>
                <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100">What {req.client_name} can do</legend>
                <div className="mt-2"><ScopePicker idPrefix="consent" value={scopes} onChange={setScopes} shown={orderScopes(req.scopes)} grantable={grantable} /></div>
                {scopes.includes('launch') ? (
                    <div className="mt-3">
                        <LaunchWarning limitLink={(
                            <a href={`${RouteConstants.MARKETING_SETTINGS}#${LAUNCH_LIMIT_ID}`} target="_blank" rel="noopener noreferrer" className="font-medium underline underline-offset-2">See the launch limit</a>
                        )} />
                        {typeof req.launch_limit === 'number' ? (
                            <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-400">
                                {req.launch_limit > 0
                                    ? `It could email up to ${req.launch_limit.toLocaleString()} people at a time without review.`
                                    : 'The launch limit is 0, so it still can’t send anything without review.'}
                            </p>
                        ) : null}
                    </div>
                ) : null}
            </fieldset>

            <fieldset>
                <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Stays connected for</legend>
                <div className="mt-1.5 flex flex-wrap gap-1" role="radiogroup" aria-label="Stays connected for">
                    {EXPIRY_CHOICES.map((d) => (
                        <button key={d} type="button" role="radio" aria-checked={days === d} className={chipClass(days === d)} onClick={() => setDays(d)}>{d} days</button>
                    ))}
                </div>
            </fieldset>

            <p className="flex items-start gap-2 rounded-lg bg-surface-muted px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300">
                <ArrowUturnLeftIcon className="mt-px h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
                <span>You&apos;ll be sent back to <span className="font-medium text-zinc-900 dark:text-zinc-100">{req.redirect_host}</span>.</span>
            </p>

            {error ? <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button plain disabled={!!busy} onClick={() => void finish('cancel')}>{busy === 'cancel' ? 'Cancelling…' : 'Cancel'}</Button>
                <Button color={scopes.includes('launch') ? 'red' : 'emerald'} disabled={!!busy} onClick={() => void finish('allow')}>{busy === 'allow' ? 'Connecting…' : 'Allow'}</Button>
            </div>

            <p className="text-center text-xs text-zinc-500 dark:text-zinc-400">
                Signed in as {email}. <button type="button" onClick={onSwitchUser} className="font-medium text-emerald-700 hover:underline dark:text-emerald-400">Use another account</button>
            </p>
        </div>
    );
};

// #region MarketingConnect
/**
 * /marketing/connect?request=…: the consent page an app (claude.ai, ChatGPT) opens through OAuth.
 * Signed in, outside the dashboard. Signed-out visitors go to /login and come back with the query intact.
 */
const MarketingConnect: React.FunctionComponent<{}> = (): React.ReactElement | null => {
    const [user, , loginFunctions] = useLogin();
    const [api] = useMarketing();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const request = new URLSearchParams(search).get('request') ?? '';
    const loginHref = `/login?ref=${encodeURIComponent(pathname + search)}`;
    const [req, setReq] = React.useState<MktOAuthRequestView | MktError | null>(null);
    const apiRef = React.useRef(api);
    apiRef.current = api;

    React.useEffect(() => { document.title = 'Wallmates | Connect an app'; }, []);
    React.useEffect(() => { if (!user) navigate(loginHref, { replace: true }); }, [user, loginHref, navigate]);

    React.useEffect(() => {
        if (!user || !request) return undefined;
        let live = true;
        void apiRef.current.oauthRequest(request).then((r) => {
            if (!live) return;
            // An expired session signs out on 401: come back here after signing in again.
            if (isMktError(r) && r.status === 401) { navigate(loginHref, { replace: true }); return; }
            setReq(r);
        });
        return () => { live = false; };
        // Load once per request and person.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, request]);

    if (!user) return null;

    const switchUser = (): void => {
        loginFunctions.logout();
        navigate(loginHref, { replace: true });
    };

    let body: React.ReactNode;
    if (!user.email_verified) {
        body = <PublicMessage title="Verify your email first" body="Open the link Wallmates emailed you, then come back to this page." />;
    } else if (!request) {
        body = <PublicMessage title="This link is not complete" body="Start connecting again from the app you were using." />;
    } else if (req === null) {
        body = <div className="px-6 py-14 text-center text-sm text-zinc-500 dark:text-zinc-400" role="status">Loading…</div>;
    } else if (isMktError(req)) {
        body = req.status === 403 ? (
            <PublicMessage title="Ask a sales manager" body={req.message || 'Only a sales manager can connect an app to the marketing workspace. Send them this page’s link.'}>
                <LockClosedIcon className="mx-auto mt-4 h-5 w-5 text-zinc-400" aria-hidden="true" />
                <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
                    Signed in as {user.email}. <button type="button" onClick={switchUser} className="font-medium text-emerald-700 hover:underline dark:text-emerald-400">Use another account</button>
                </p>
            </PublicMessage>
        ) : (
            <PublicMessage title="This request can't be used" body={req.message || 'It may have expired. Start connecting again from the app you were using.'} />
        );
    } else {
        body = <ConsentForm request={request} req={req} email={user.email} onSwitchUser={switchUser} />;
    }

    return <PublicShell>{body}</PublicShell>;
};

export default MarketingConnect;
